import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { CallSessionProvider } from "@/components/messaging/calls/CallSessionContext";
import { SocketProvider } from "@/contexts/SocketContext";

// Auth Pages
import LandingPage from "@/pages/LandingPage";
import BRMLogin from "@/pages/brm/BRMLogin";
import BRMPINSetup from "@/pages/brm/BRMPINSetup";
import ClientLogin from "@/pages/client/ClientLogin";
import ClientRegister from "@/pages/client/ClientRegister";

// BRM Pages
import BRMDashboard from "@/pages/brm/BRMDashboard";
import BRMEnrollUser from "@/pages/brm/BRMEnrollUser";
import BRMMyUsers from "@/pages/brm/BRMMyUsers";
import BRMWallet from "@/pages/brm/BRMWallet";
import BRMProfile from "@/pages/brm/BRMProfile";
import BRMReferrals from "@/pages/brm/BRMReferrals";
import BRMMessages from "@/pages/brm/BRMMessages";
import BRMSocialProfile from "@/pages/brm/BRMSocialProfile";
import BRMCampaigns from "@/pages/brm/BRMCampaigns";
import BRMCampaignDetail from "@/pages/brm/BRMCampaignDetail";
import BRMCampaignHub from "@/pages/brm/BRMCampaignHub";
import BRMOnboarding from "@/pages/brm/BRMOnboarding";

// Client Pages
import ClientDashboard from "@/pages/client/ClientDashboard";
import ClientBRMs from "@/pages/client/ClientBRMs";
import ClientEnrollments from "@/pages/client/ClientEnrollments";
import ClientSettings from "@/pages/client/ClientSettings";
import ClientProducts from "@/pages/client/ClientProducts";
import ClientProjects from "@/pages/client/ClientProjects";
import ClientIntegrationBuilder from "@/pages/client/ClientIntegrationBuilder";
import ClientWebhookLogs from "@/pages/client/ClientWebhookLogs";
import ClientMessages from "@/pages/client/ClientMessages";
import ClientSDKSetup from "@/pages/client/ClientSDKSetup";
import ClientCampaigns from "@/pages/client/ClientCampaigns";
import ClientCampaignCreate from "@/pages/client/ClientCampaignCreate";
import ClientCampaignDetail from "@/pages/client/ClientCampaignDetail";
import ClientPackages from "@/pages/client/ClientPackages";
import ProveRequestCreate from "@/pages/client/ProveRequestCreate";
import ProveRequestSetup from "@/pages/client/ProveRequestSetup";
import ClientOnboarding from "@/pages/client/ClientOnboarding";
import ClientPaymentReturn from "@/pages/client/ClientPaymentReturn";

// Admin Pages
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminClients from "@/pages/admin/AdminClients";
import AdminBRMs from "@/pages/admin/AdminBRMs";
import AdminEnrollments from "@/pages/admin/AdminEnrollments";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminOperations from "@/pages/admin/AdminOperations";
import AdminStaffInvitations from "@/pages/admin/AdminStaffInvitations";
import AdminAcceptInvitation from "@/pages/admin/AdminAcceptInvitation";
import AdminResetPassword from "@/pages/admin/AdminResetPassword";
import AdminRequestPasswordRecovery from "@/pages/admin/AdminRequestPasswordRecovery";
import AdminStaff from "@/pages/admin/AdminStaff";

// Components
import BRMLayout from "@/components/layout/BRMLayout";
import ClientLayout from "@/components/layout/ClientLayout";
import ClientOnboardingLayout from "@/components/layout/ClientOnboardingLayout";
import AdminLayout from "@/components/layout/AdminLayout";

// Services
import { brmAPI, clientAPI, setAuthToken } from "@/services/api";
import { subscribeToOnlineStatus } from "@/services/offline";
import { clearClientOnboardingDrafts } from "@/lib/proveRequestForm";

function App() {
  // Hydrate authentication synchronously so a browser refresh on a protected
  // deep link does not briefly render the logged-out routes and discard the
  // user's destination before the mount effect runs.
  const persistedToken = localStorage.getItem("proovam_token");
  const persistedUserType = localStorage.getItem("proovam_user_type");
  const [isAuthenticated, setIsAuthenticated] = useState(
    Boolean(persistedToken && persistedUserType)
  );
  const [userType, setUserType] = useState(persistedUserType);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [requiresPINSetup, setRequiresPINSetup] = useState(
    localStorage.getItem("proovam_requires_pin") === "true"
  );
  const [onboardingComplete, setOnboardingComplete] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(false);
  const [profileReloadKey, setProfileReloadKey] = useState(0);

  useEffect(() => {
    // Check for existing auth
    const token = localStorage.getItem("proovam_token");
    const storedUserType = localStorage.getItem("proovam_user_type");
    const needsPIN = localStorage.getItem("proovam_requires_pin") === "true";
    
    if (token && storedUserType) {
      setAuthToken(token);
      setIsAuthenticated(true);
      setUserType(storedUserType);
      setRequiresPINSetup(needsPIN);
    }

    // Subscribe to online status changes
    const unsubscribe = subscribeToOnlineStatus(setIsOnline);
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !userType) {
      setOnboardingComplete(null);
      return;
    }

    let cancelled = false;
    setProfileLoading(true);
    setProfileError(false);
    const request = userType === "brm" ? brmAPI.getProfile() : clientAPI.getProfile();
    request
      .then((response) => {
        if (cancelled) return;
        const profile = response.data || {};
        const complete =
          userType === "brm"
            ? Boolean(profile.onboardingCompletedAt || profile.onboarding_completed_at)
            : Boolean(profile.onboarding_complete);
        setOnboardingComplete(complete);
        const userId = profile.brmId || profile.brm_id || profile.id || profile.clientId;
        if (userId) localStorage.setItem("user_id", userId);
      })
      .catch(() => {
        // Fail closed: a transient profile-fetch error must NOT drop an
        // established user back into onboarding (risking duplicate offers).
        if (!cancelled) setProfileError(true);
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, userType, profileReloadKey]);

  const handleLogin = (token, type, needsPINSetup = false) => {
    setAuthToken(token);
    localStorage.setItem("proovam_user_type", type);
    localStorage.setItem("proovam_requires_pin", needsPINSetup.toString());
    setIsAuthenticated(true);
    setUserType(type);
    setRequiresPINSetup(needsPINSetup);
    setOnboardingComplete(null);
  };

  const handlePINSetupComplete = () => {
    localStorage.setItem("proovam_requires_pin", "false");
    setRequiresPINSetup(false);
    setOnboardingComplete(null);
    localStorage.removeItem("user_id");
    setProfileReloadKey((key) => key + 1);
  };

  const handleLogout = () => {
    setAuthToken(null);
    clearClientOnboardingDrafts();
    localStorage.removeItem("proovam_user_type");
    localStorage.removeItem("proovam_requires_pin");
    localStorage.removeItem("proovam_requires_phone");
    localStorage.removeItem("user_id");
    setIsAuthenticated(false);
    setUserType(null);
    setRequiresPINSetup(false);
  };

  const brmPostAuthPath = () => {
    if (requiresPINSetup) return "/brm/pin-setup";
    if (localStorage.getItem("proovam_requires_phone") === "true") return "/brm/onboarding";
    return onboardingComplete ? "/brm/dashboard" : "/brm/onboarding";
  };

  const markOnboardingComplete = () => setOnboardingComplete(true);

  const profileErrorScreen = (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center">
      <p className="text-sm text-gray-600">
        We couldn't load your account. Check your connection and try again.
      </p>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => setProfileReloadKey((k) => k + 1)}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white"
        >
          Retry
        </button>
        <button
          type="button"
          onClick={handleLogout}
          className="rounded-md border px-4 py-2 text-sm font-medium"
        >
          Log out
        </button>
      </div>
    </div>
  );

  return (
    <SocketProvider>
      <div className="App min-h-screen bg-background">
        <BrowserRouter>
          {/* Offline indicator */}
          {!isOnline && (
            <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white text-center text-sm py-1 z-50" data-testid="offline-indicator">
              You're offline. Some features may be limited.
            </div>
          )}

        <CallSessionProvider key={`${userType}:${persistedToken || "signed-out"}`} enabled={isAuthenticated && ["brm", "client"].includes(userType)} token={persistedToken} role={userType}>
        <Routes>
          {process.env.NODE_ENV === "development" ? (
            <Route
              path="/_preview/client-onboarding"
              element={
                <ClientOnboardingLayout onLogout={handleLogout}>
                  <ClientOnboarding clientId="preview" />
                </ClientOnboardingLayout>
              }
            />
          ) : null}

          {/* Public Routes */}
          <Route
            path="/"
            element={
              isAuthenticated ? (
              requiresPINSetup && userType === "brm" ? (
                <Navigate to="/brm/pin-setup" replace />
              ) : userType === "brm" ? (
                <Navigate to={brmPostAuthPath()} replace />
              ) : onboardingComplete ? (
                  <Navigate to="/client/dashboard" replace />
                ) : (
                  <Navigate to="/client/onboarding" replace />
              )
              ) : (
                <LandingPage />
              )
            }
          />

          {/* BRM Auth Routes */}
          <Route
            path="/brm/login"
            element={
              isAuthenticated && userType === "brm" ? (
                requiresPINSetup ? (
                  <Navigate to="/brm/pin-setup" replace />
                ) : (
                  <Navigate to={brmPostAuthPath()} replace />
                )
              ) : (
                <BRMLogin onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/brm/pin-setup"
            element={
              isAuthenticated && userType === "brm" && requiresPINSetup ? (
                <BRMPINSetup onComplete={handlePINSetupComplete} />
              ) : (
                <Navigate to="/brm/dashboard" replace />
              )
            }
          />

          {/* Client Auth Routes */}
          <Route
            path="/client/login"
            element={
              isAuthenticated && userType === "client" ? (
                onboardingComplete ? (
                  <Navigate to="/client/dashboard" replace />
                ) : (
                  <Navigate to="/client/onboarding" replace />
                )
              ) : (
                <ClientLogin onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/client/register"
            element={
              isAuthenticated && userType === "client" ? (
                onboardingComplete ? (
                  <Navigate to="/client/dashboard" replace />
                ) : (
                  <Navigate to="/client/onboarding" replace />
                )
              ) : (
                <ClientRegister onLogin={handleLogin} />
              )
            }
          />

          {/* BRM Protected Routes */}
          <Route
            path="/brm/*"
            element={
              isAuthenticated && userType === "brm" && !requiresPINSetup ? (
                profileError ? (
                  profileErrorScreen
                ) : profileLoading || onboardingComplete === null ? (
                  <div className="p-8 text-center text-sm text-gray-500">Loading your account…</div>
                ) : (
                <BRMLayout onLogout={handleLogout} isOnline={isOnline}>
                  <Routes>
                    {onboardingComplete === false ? (
                      <>
                        <Route path="onboarding" element={<BRMOnboarding onComplete={markOnboardingComplete} />} />
                        <Route path="social" element={<BRMSocialProfile />} />
                        <Route path="*" element={<Navigate to="/brm/onboarding" replace />} />
                      </>
                    ) : (
                      <>
                        <Route path="onboarding" element={<Navigate to="/brm/dashboard" replace />} />
                        <Route path="dashboard" element={<BRMDashboard isOnline={isOnline} />} />
                        <Route path="enroll" element={<BRMEnrollUser isOnline={isOnline} />} />
                        <Route path="users" element={<BRMMyUsers />} />
                        <Route path="wallet" element={<BRMWallet />} />
                        <Route path="profile" element={<BRMProfile onLogout={handleLogout} />} />
                        <Route path="referrals" element={<BRMReferrals />} />
                        <Route path="messages" element={<BRMMessages />} />
                        <Route path="social" element={<BRMSocialProfile />} />
                        <Route path="prove-requests" element={<Navigate to="/brm/campaigns" replace />} />
                        <Route path="products" element={<Navigate to="/brm/campaigns" replace />} />
                        <Route path="campaigns" element={<BRMCampaigns />} />
                        <Route path="campaigns/:campaignId" element={<BRMCampaignDetail />} />
                        <Route path="campaigns/:campaignId/hub" element={<BRMCampaignHub />} />
                        <Route path="*" element={<Navigate to="/brm/dashboard" replace />} />
                      </>
                    )}
                  </Routes>
                </BRMLayout>
                )
              ) : (
                <Navigate to="/brm/login" replace />
              )
            }
          />

          {/* Client Protected Routes */}
          <Route
            path="/client/*"
            element={
              isAuthenticated && userType === "client" ? (
                profileError ? (
                  profileErrorScreen
                ) : profileLoading || onboardingComplete === null ? (
                  <div className="p-8 text-center text-sm text-gray-500">Loading your account…</div>
                ) : (
                onboardingComplete === false ? (
                  <ClientOnboardingLayout onLogout={handleLogout}>
                    <Routes>
                      <Route path="onboarding" element={<ClientOnboarding clientId={localStorage.getItem("user_id")} onComplete={markOnboardingComplete} />} />
                      <Route path="payments/return" element={<ClientPaymentReturn clientId={localStorage.getItem("user_id")} onComplete={markOnboardingComplete} />} />
                      <Route path="*" element={<Navigate to="/client/onboarding" replace />} />
                    </Routes>
                  </ClientOnboardingLayout>
                ) : (
                  <ClientLayout onLogout={handleLogout}>
                    <Routes>
                      <Route path="onboarding" element={<Navigate to="/client/dashboard" replace />} />
                      <Route path="payments/return" element={<ClientPaymentReturn clientId={localStorage.getItem("user_id")} onComplete={markOnboardingComplete} />} />
                      <Route path="dashboard" element={<ClientDashboard />} />
                      <Route path="brms" element={<ClientBRMs />} />
                      <Route path="enrollments" element={<ClientEnrollments />} />
                      <Route path="products" element={<ClientProducts />} />
                      <Route path="projects" element={<ClientProjects />} />
                      <Route path="prove-requests/create" element={<ProveRequestCreate />} />
                      <Route path="prove-requests/:productId/setup" element={<ProveRequestSetup />} />
                      <Route path="products/:productId/integration" element={<ClientIntegrationBuilder />} />
                      <Route path="webhook-logs" element={<ClientWebhookLogs />} />
                      <Route path="settings" element={<ClientSettings />} />
                      <Route path="messages" element={<ClientMessages />} />
                      <Route path="sdk" element={<ClientSDKSetup />} />
                      <Route path="campaigns" element={<ClientCampaigns />} />
                      <Route path="campaigns/create" element={<ClientCampaignCreate />} />
                      <Route path="products/:productId/campaigns" element={<ClientCampaigns />} />
                      <Route path="products/:productId/campaigns/create" element={<ClientCampaignCreate />} />
                      <Route path="campaigns/:campaignId" element={<ClientCampaignDetail />} />
                      <Route path="packages" element={<ClientPackages />} />
                      <Route path="*" element={<Navigate to="/client/dashboard" replace />} />
                    </Routes>
                  </ClientLayout>
                )
                )
              ) : (
                <Navigate to="/client/login" replace />
              )
            }
          />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/accept-invitation" element={<AdminAcceptInvitation />} />
          <Route path="/admin/reset-password" element={<AdminResetPassword />} />
          <Route path="/admin/forgot-password" element={<AdminRequestPasswordRecovery />} />
          <Route
            path="/admin/*"
            element={
              <AdminLayout>
                <Routes>
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="clients" element={<AdminClients />} />
                  <Route path="brms" element={<AdminBRMs />} />
                  <Route path="enrollments" element={<AdminEnrollments />} />
                  <Route path="analytics" element={<AdminAnalytics />} />
                  <Route path="operations" element={<AdminOperations />} />
                  <Route path="staff-invitations" element={<AdminStaffInvitations />} />
                  <Route path="staff" element={<AdminStaff />} />
                  <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
                </Routes>
              </AdminLayout>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </CallSessionProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
    </SocketProvider>
  );
}

export default App;
